import { motion, useMotionValue, useTransform } from 'framer-motion'
import Tilt from 'react-parallax-tilt'
import { useRef } from 'react'

const PILLARS = [
  {
    num: '01',
    title: 'Nothing off the shelf',
    body: 'Everything is built from scratch around what you actually need. No page-builder templates, no recycled resources with our name slapped on top.',
    accent: '#22d3ee',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
      </svg>
    ),
  },
  {
    num: '02',
    title: 'Payments, sorted',
    body: 'Stripe wired up properly — checkout, subscriptions, webhooks, customer portals. The whole flow, done right.',
    accent: '#22d3ee',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    ),
  },
  {
    num: '03',
    title: 'Proven in production',
    body: 'Nothing ships on a promise. Every site gets pushed through real devices and real testing before release.',
    accent: '#22d3ee',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>
      </svg>
    ),
  },
  {
    num: '04',
    title: 'Fast & clean code',
    body: 'Every project gets the same bar. No bloat, no shortcuts. Everything loads fast, runs lean, and stays clean underneath.',
    accent: '#a78bfa',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
      </svg>
    ),
  },
]

function SpotlightCard({ p, i }) {
  const cardRef = useRef(null)
  const rectRef = useRef(null)
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)

  // Reading getBoundingClientRect() on every mousemove forces a synchronous
  // layout recalculation whenever anything else on the page just wrote a
  // style — cache it once when the pointer enters instead, since the card
  // doesn't move or resize during a hover.
  const handleMouseEnter = () => {
    rectRef.current = cardRef.current?.getBoundingClientRect() ?? null
  }
  const handleMouseMove = (e) => {
    const rect = rectRef.current
    if (!rect) return
    mouseX.set(e.clientX - rect.left)
    mouseY.set(e.clientY - rect.top)
  }
  const handleMouseLeave = () => {
    mouseX.set(-999)
    mouseY.set(-999)
    rectRef.current = null
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
    >
    <Tilt
      tiltMaxAngleX={8} tiltMaxAngleY={8}
      glareEnable={true} glareMaxOpacity={0.06}
      glareColor={p.accent} glarePosition="all"
      scale={1.02} transitionSpeed={600}
      style={{ height: '100%', borderRadius: 16 }}
    >
    <div
      ref={cardRef}
      className="pillar-card"
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'relative',
        padding: '32px 28px',
        borderRadius: 16,
        height: '100%',
        background: p.accent === '#a78bfa'
          ? 'rgba(139,92,246,0.03)'
          : 'rgba(255,255,255,0.02)',
        border: `1px solid ${p.accent === '#a78bfa' ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.06)'}`,
        overflow: 'hidden',
        transition: 'border-color 0.3s',
      }}
    >
      {/* Spotlight glow that follows cursor */}
      <motion.div
        style={{
          position: 'absolute',
          width: 200, height: 200,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${p.accent}18 0%, transparent 70%)`,
          pointerEvents: 'none',
          x: useTransform(mouseX, v => v - 100),
          y: useTransform(mouseY, v => v - 100),
          zIndex: 0,
        }}
      />

      {/* Top accent line */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg, transparent, ${p.accent}50, transparent)`,
        opacity: 0, transition: 'opacity 0.3s', zIndex: 1,
      }} className="pillar-top-line" />

      {/* Number */}
      <div style={{
        position: 'absolute', top: 20, right: 20, zIndex: 1,
        fontFamily: 'monospace', fontSize: 10,
        color: `${p.accent}cc`, letterSpacing: '.1em',
      }}>
        {p.num}
      </div>

      {/* Icon */}
      <div style={{
        width: 40, height: 40, borderRadius: 10, marginBottom: 24,
        background: `${p.accent}10`,
        border: `1px solid ${p.accent}20`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: p.accent, position: 'relative', zIndex: 1,
      }}>
        {p.icon}
      </div>

      {/* Title */}
      <h3 style={{
        fontFamily: 'Geist Variable, sans-serif',
        fontSize: 16, fontWeight: 600, color: 'rgba(255,255,255,0.88)',
        letterSpacing: '-0.02em', marginBottom: 12, lineHeight: 1.3,
        position: 'relative', zIndex: 1,
      }}>
        {p.title}
      </h3>

      {/* Body */}
      <p style={{
        fontSize: 13, color: 'rgba(255,255,255,0.5)',
        lineHeight: 1.75, position: 'relative', zIndex: 1,
      }}>
        {p.body}
      </p>
    </div>
    </Tilt>
    </motion.div>
  )
}

export default function Stats() {
  return (
    <section style={{
      padding: '120px 0',
      background: '#000',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Top rule */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 1,
        background: 'linear-gradient(90deg, transparent, rgba(6,182,212,0.18), transparent)',
      }} />

      {/* Centered glow */}
      <div className="bg-orb" style={{
        width: 700, height: 350,
        background: 'rgba(6,182,212,0.05)',
        top: '50%', left: '50%',
        transform: 'translate(-50%,-50%)',
        filter: 'blur(100px)',
      }} />

      {/* Faded watermark — purely decorative, hidden from a11y tree */}
      <div aria-hidden="true" style={{
        position: 'absolute', right: '-1%', top: '50%',
        transform: 'translateY(-50%)',
        fontFamily: 'Geist Variable, sans-serif',
        fontSize: 'clamp(160px, 22vw, 340px)',
        fontWeight: 800, color: 'rgba(6,182,212,0.018)',
        lineHeight: 1, pointerEvents: 'none', userSelect: 'none',
        letterSpacing: '-0.06em',
      }}>
        CJM
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 32px', position: 'relative', zIndex: 2 }}>

        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          style={{ marginBottom: 72 }}
        >
          <h2 className="display-lg heading-glow" style={{ color: '#fff', lineHeight: 1.0 }}>
            Built to a standard, <span style={{ color: 'rgba(255,255,255,0.5)' }}>not a deadline.</span>
          </h2>
          <p style={{
            color: 'rgba(255,255,255,0.5)', fontSize: 15,
            maxWidth: 380, lineHeight: 1.8, marginTop: 16,
          }}>
            The same rules apply to everything we ship — whether it's a full website or a single component.
          </p>
        </motion.div>

        {/* Cards grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 12,
        }} className="pillars-grid">
          {PILLARS.map((p, i) => (
            <SpotlightCard key={p.title} p={p} i={i} />
          ))}
        </div>
      </div>

      <style>{`
        .pillar-card:hover .pillar-top-line { opacity: 1 !important; }
        @media(max-width: 900px) {
          .pillars-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media(max-width: 480px) {
          .pillars-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  )
}
